<template>
  <div class="grid-layout">
    <section class="user-info grid-layout__main">
      <section class="user-info__info card">
        <div class="user-info__header">
          <h2 class="title-large">{{ $t('components.userInfo.details') }}</h2>
          <button
            v-if="!isEditing"
            class="button button--small button--secondary"
            @click="startEditing"
          >
            {{ $t('components.userInfo.editProfile') }}
          </button>
        </div>
        <div
          v-if="!isEditing"
          class="user-info__show-info"
        >
          <div class="user-info__show-info-row">
            <span class="user-info__show-info-definition">{{ $t('components.userInfo.name') }}</span>
            <span>{{ user?.first_name }} {{ user?.last_name }}</span>
          </div>
          <div class="user-info__show-info-row">
            <span class="user-info__show-info-definition">{{ $t('components.userInfo.email') }}</span>
            <span>{{ user?.email }}</span>
          </div>
          <div class="user-info__show-info-row">
            <span class="user-info__show-info-definition">{{ $t('components.userInfo.phone') }}</span>
            <span>{{ user?.phone_numbers[0]?.value }}</span>
          </div>
          <div class="user-info__show-info-row">
            <span class="user-info__show-info-definition">{{ $t('components.userInfo.address') }}</span>
            <span
              v-for="(address, index) in user?.addresses"
              :key="index"
            >
              {{ address.street }} {{ address.house_number }}
              <br />
              {{ address.postal_code }}
              {{ address.city }}
            </span>
          </div>
        </div>
        <div
          v-else
          class="user-info__edit-info"
        >
          <PersonalInfoForm
            id-prefix="edit-profile"
            v-model:user="editedUser"
            :hasAddress="true"
          />
          <Input
            id="edit-profile-password"
            :label="$t('components.userInfo.password')"
            type="password"
            v-model="password"
            :placeholder="$t('components.userInfo.password')"
            :internalLabel="true"
          />
          <div class="user-info__actions">
            <button
              class="button button--medium button--primary"
              :class="{'disabled': !hasChanges && !password}"
              :disabled="!hasChanges && !password"
              @click="saveChanges"
            >
              {{ $t('components.userInfo.save') }}
            </button>
            <button
              class="button button--medium button--secondary"
              @click="cancelEdit"
            >
              {{ $t('components.userInfo.cancel') }}
            </button>
          </div>
        </div>
      </section>
    </section>

    <aside class="grid-layout__sidebar">
      <ul class="card link-list">
        <li class="link-list__item">
          <button
            class="link-list__button"
            @click="showPasswordModal = true"
          >
            <svg class="link-list__button-icon">
              <use :xlink:href="`#${lock}`" />
            </svg>
            <span class="link-list__button-text">{{ $t('components.userInfo.changePassword') }}</span>
            <svg class="link-list__chevron">
              <use :xlink:href="`#${chevron}`" />
            </svg>
          </button>
        </li>
        <li class="link-list__item">
          <button
            class="link-list__button"
            @click="handleLogout"
          >
            <svg class="link-list__button-icon">
              <use :xlink:href="`#${logout}`" />
            </svg>
            <span class="link-list__button-text">{{ $t('components.userInfo.logout') }}</span>
            <svg class="link-list__chevron">
              <use :xlink:href="`#${chevron}`" />
            </svg>
          </button>
        </li>
        <li class="link-list__item">
          <button
            class="link-list__button link-list__button--danger"
            @click="showDeleteModal = true"
          >
            <svg class="link-list__button-icon">
              <use :xlink:href="`#${trash}`" />
            </svg>
            <span class="link-list__button-text">{{ $t('components.userInfo.deleteAccount') }}</span>
            <svg class="link-list__chevron">
              <use :xlink:href="`#${chevron}`" />
            </svg>
          </button>
        </li>
      </ul>
    </aside>

    <Modal
      v-if="showPasswordModal"
      @close="showPasswordModal = false"
    >
      <template #header>
        <h2>{{ $t('components.userInfo.changePassword') }}</h2>
      </template>
      <template #default>
        <section class="user-info__password">
          <h2 class="user-info__password-headline headline-small">{{ $t('components.userInfo.changePassword') }}</h2>
          <Input
            v-model="password"
            id="current-password"
            type="password"
            :label="$t('components.userInfo.currentPassword')"
            :placeholder="$t('components.userInfo.currentPassword')"
            :error="errors.password"
          />
          <Input
            v-model="newPassword"
            id="new-password"
            type="password"
            :label="$t('components.userInfo.newPassword')"
            :placeholder="$t('components.userInfo.newPassword')"
            :error="errors.newPassword"
          />
          <Input
            v-model="confirmPassword"
            id="confirm-password"
            type="password"
            :label="$t('components.userInfo.confirmPassword')"
            :placeholder="$t('components.userInfo.confirmPassword')"
            :error="errors.confirmPassword"
          />
          <button
            class="button button--medium button--primary"
            @click="updatePassword"
            :disabled="loading"
          >
            {{ loading ? $t('components.userInfo.updating') : $t('components.userInfo.updatePassword') }}
          </button>
        </section>
      </template>
    </Modal>

    <ConfirmationModal
      :is-open="showDeleteModal"
      :heading="$t('components.userInfo.deleteAccount')"
      :confirm-text="$t('components.userInfo.deleteAccount')"
      @cancel="showDeleteModal = false"
      @confirm="handleDeleteAccount"
    >
      <div class="user-info__delete-message">
        <p>{{ $t('components.userInfo.deleteAccountConfirmation') }}</p>
        <p>{{ $t('components.userInfo.deleteAccountInfo') }}</p>
        <ul>
          <li>{{ $t('components.userInfo.personalInfo') }}</li>
          <li>{{ $t('components.userInfo.bookingHistory') }}</li>
          <li>{{ $t('components.userInfo.paymentInfo') }}</li>
        </ul>
        <p>{{ $t('components.userInfo.deleteAccountWarning') }}</p>
      </div>
    </ConfirmationModal>
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref, computed, onMounted } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useUserStore } from '@/stores/userStore'
  import PersonalInfoForm from '@/components/BaseComponents/PersonalInfoForm.vue'
  import Input from '@/components/BaseComponents/Input.vue'
  import Modal from '@/components/BaseComponents/Modal/Modal.vue'
  import Button from '@/components/BaseComponents/Button.vue'
  import ConfirmationModal from '@/components/BaseComponents/Modal/ConfirmationModal.vue'
  import { updateUserDetails, deleteAccount } from '@/services/userService'
  import { useRouter } from 'vue-router'
  import lock from '@/assets/icons/lock.svg'
  import logout from '@/assets/icons/logout.svg'
  import trash from '@/assets/icons/trash.svg'
  import chevron from '@/assets/icons/chevron.svg'

  interface EditableUser {
    first_name: string
    last_name: string
    email: string
    phone_number: string
    street: string
    house_number: string
    postal_code: string
    city: string
    password?: string
  }

  interface UpdateUserData {
    first_name: string
    last_name: string
    email: string
    phone_numbers: { value: string; preferred: boolean; type: string }[]
    addresses: {
      street: string
      house_number: string
      postal_code: string
      city: string
    }[]
    password?: string
  }

  export default defineComponent({
    name: 'UserInfo',
    components: {
      PersonalInfoForm,
      Input,
      Modal,
      Button,
      ConfirmationModal,
    },
    setup() {
      const { t } = useI18n()
      const userStore = useUserStore()
      const router = useRouter()
      const isEditing = ref(false)
      const password = ref('')
      const showSuccessModal = ref(false)
      const showDeleteModal = ref(false)

      onMounted(() => {
        showSuccessModal.value = false
        showDeleteModal.value = false
      })

      const editedUser = ref<EditableUser>({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        street: '',
        house_number: '',
        postal_code: '',
        city: '',
      })

      const user = computed(() => userStore.user)

      const hasChanges = computed(() => {
        if (!user.value) return false

        return (
          editedUser.value.first_name !== user.value.first_name ||
          editedUser.value.last_name !== user.value.last_name ||
          editedUser.value.email !== user.value.email ||
          editedUser.value.phone_number !==
            user.value.phone_numbers?.[0]?.value ||
          editedUser.value.street !== user.value.addresses?.[0]?.street ||
          editedUser.value.house_number !==
            user.value.addresses?.[0]?.house_number ||
          editedUser.value.postal_code !==
            user.value.addresses?.[0]?.postal_code ||
          editedUser.value.city !== user.value.addresses?.[0]?.city
        )
      })

      const startEditing = () => {
        if (!user.value) return
        const address = user.value.addresses?.[0] || {}
        editedUser.value = {
          first_name: user.value.first_name,
          last_name: user.value.last_name,
          email: user.value.email,
          phone_number: user.value.phone_numbers?.[0]?.value || '',
          street: address.street || '',
          house_number: address.house_number || '',
          postal_code: address.postal_code || '',
          city: address.city || '',
        }
        isEditing.value = true
      }

      const cancelEdit = () => {
        isEditing.value = false
        password.value = ''
      }

      const saveChanges = async () => {
        try {
          const updateData: UpdateUserData = {
            first_name: editedUser.value.first_name,
            last_name: editedUser.value.last_name,
            email: editedUser.value.email,
            phone_numbers: [
              {
                value: editedUser.value.phone_number,
                preferred: true,
                type: 'privateMobile',
              },
            ],
            addresses: [
              {
                street: editedUser.value.street,
                house_number: editedUser.value.house_number,
                postal_code: editedUser.value.postal_code,
                city: editedUser.value.city,
              },
            ],
          }
          if (password.value) {
            updateData.password = password.value
          }

          if (!userStore.token) {
            throw new Error(t('components.userInfo.errors.noToken'))
          }

          const updatedUser = await updateUserDetails(updateData, userStore.token)
          userStore.setUser(updatedUser.data.customer)
          isEditing.value = false
          password.value = ''
        } catch (error) {
          console.error('Error updating user details:', error)
        }
      }

      const loading = ref(false)
      const showPasswordModal = ref(false)
      const newPassword = ref('')
      const confirmPassword = ref('')
      const errors = ref({
        password: '',
        newPassword: '',
        confirmPassword: '',
      })

      const validatePasswordForm = () => {
        errors.value = {
          password: '',
          newPassword: '',
          confirmPassword: '',
        }

        if (!password.value) {
          errors.value.password = t('components.userInfo.errors.passwordRequired')
        }
        if (!newPassword.value) {
          errors.value.newPassword = t('components.userInfo.errors.newPasswordRequired')
        }
        if (!confirmPassword.value) {
          errors.value.confirmPassword = t('components.userInfo.errors.confirmPasswordRequired')
        }
        if (newPassword.value !== confirmPassword.value) {
          errors.value.confirmPassword = t('components.userInfo.errors.passwordsDoNotMatch')
        }

        return !Object.values(errors.value).some((error) => error)
      }

      const updatePassword = async () => {
        if (!validatePasswordForm() || !user.value) return

        loading.value = true
        try {
          const userData = {
            ...user.value,
            password: newPassword.value,
            current_password: password.value,
          }

          if (!userStore.token) {
            throw new Error(t('components.userInfo.errors.noToken'))
          }

          await updateUserDetails(userData, userStore.token)
          showPasswordModal.value = false
          password.value = ''
          newPassword.value = ''
          confirmPassword.value = ''
        } catch (error) {
          if (error instanceof Error) {
            if (error.message.includes('current_password')) {
              errors.value.password = t('components.userInfo.errors.incorrectPassword')
            } else {
              errors.value.newPassword = error.message
            }
          } else {
            errors.value.newPassword = t('components.userInfo.errors.updatePasswordError')
          }
        } finally {
          loading.value = false
        }
      }

      const handleLogout = () => {
        userStore.logout()
        router.push('/')
      }

      const handleDeleteAccount = async () => {
        try {
          if (!userStore.token) {
            throw new Error(t('components.userInfo.errors.noToken'))
          }
          await deleteAccount(userStore.token)
          userStore.logout()
          showSuccessModal.value = true
          router.push('/')
        } catch (error) {
          console.error('Error deleting account:', error)
        }
      }

      const handleCloseSuccessModal = () => {
        showSuccessModal.value = false
        router.push('/')
      }

      return {
        user,
        editedUser,
        isEditing,
        password,
        newPassword,
        confirmPassword,
        errors,
        loading,
        showPasswordModal,
        showDeleteModal,
        showSuccessModal,
        hasChanges,
        startEditing,
        cancelEdit,
        saveChanges,
        updatePassword,
        handleLogout,
        handleDeleteAccount,
        handleCloseSuccessModal,
        lock,
        logout,
        trash,
        chevron,
      }
    },
  })
</script>

<style lang="scss" scoped>
  @import '@/styles/grid.scss';

  .user-info {
    &__container {
      width: 100%;
    }

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: rem(24);
      padding-bottom: rem(20);
      border-bottom: 1px solid $clr-primary-100;
    }

    &__show-info {
      display: flex;
      flex-direction: column;
      gap: 24px;
      align-items: baseline;

      &-row {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;

        span {
          text-align: left;
        }
      }

      &-definition {
        @include title-medium;
      }
    }

    &__edit-info {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__actions {
      display: flex;
      gap: 1rem;
      margin-top: 1rem;
    }

    &__info {
      padding: 1.875rem;
    }

    &__delete-message {
      text-align: left;

      p {
        margin-bottom: 1rem;
      }

      ul {
        margin: 0.5rem 0 1rem 1.5rem;
        list-style-type: disc;
      }

      li {
        margin-bottom: 0.5rem;
      }
    }
  }

  /* .sidebar-links {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin: 0;
    padding: 20px;
    list-style: none;
    background-color: $clr-white;
    border-radius: 8px;
    box-shadow: $box-shadow-card;

    li:not(:first-child)::before {
      content: '';
      display: block;
      position: relative;
      top: rem(-10);
      width: 100%;
      border-top: 1px solid $clr-primary-100;
    }

    &__button {
      $el: &;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
      width: 100%;
      padding: rem(4) 4px;
      text-align: left;
      background: none;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 1rem;
      transition: background-color 0.2s;

      &:hover {
        background-color: $clr-primary-50;
      }

      &--danger:hover {
        background-color: $clr-warning-100;
      }

      &-text {
        flex-grow: 1;
        color: $clr-text;
      }

      &-icon {
        flex-grow: 0;
        width: 32px;
        height: 32px;
        color: $clr-gray;

        &-turn {
          width: 16px;
          height: 16px;
          transform: rotate(90deg);
        }
      }
    }
  } */

  .user-info__password {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 16px;

    @media screen and (min-width: $breakpoint-xs) {
      gap: 24px;
      padding: 30px;
    }

    &-headline {
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  .success-modal {
    text-align: center;
    padding: 2rem;

    h2 {
      margin-bottom: 2rem;
    }
  }
</style>
